// The model IS the contract. camelCase fields mirror how the API DTO will
// serialize. On promote this file moves to src/models/FlowOfFunds/FlowOfFunds.ts
// and the backend Contract classes (Reports.Modules/FlowOfFunds/Api/Contracts/)
// must match it field-for-field.
//
// Money is a `number` of USD (C# decimal). Dates are ISO strings (C# DateTime /
// DateOnly). Both follow the existing marketplace contracts — the UI formats.
import { TypeOfConsideration } from '../../slices/marketplace/pricing/models/type-of-consideration.enum';
import { ConsiderationTreatment } from '../../slices/marketplace/pricing/models/consideration-treatment.enum';
import { LevelOfService } from '../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { TimelineParty } from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TypesOfConsiderationRow } from '../../slices/marketplace/loi-comparison/models/types-of-consideration-row.model';
import { VvConnectFeesDto } from '../../slices/marketplace/loi-comparison/models/vv-connect-fees.model';
import { SfasFeeParams } from '../../slices/marketplace/loi-comparison/consideration-fees';
import { ApprovalState, MarketplaceParty } from './party-approval.model';
import { WireInstructions } from './wire-instructions.model';
import { WireConfirmation } from './wire-confirmation.model';

export enum ConsiderationSection {
    Summary = 0,
    DeferredFixedPayments = 1,
    DeferredVariablePayments = 2,
    JvEquity = 3,
    CommonEquity = 4,
    PreferredEquity = 5,
}

export enum ConsiderationRowState {
    Established = 0,
    AddedPendingApproval = 1,
    RemovalRequested = 2,
    Removed = 3,
}

export enum ConsiderationField {
    FaceValue = 0,
    FairValue = 1,
    EmployeePortion = 2,
    BuyerEmployeePortion = 3,
    ProjectedCashFlow = 4,
    FeePaymentTiming = 5,
    IsConsiderationActive = 6,
    ValueAtClosing = 7,
    Amount = 8,
    PaymentTiming = 9,
    AnnualInterestAccrualRate = 10,
    IsContingent = 11,
    ContingencyDependentVariable = 12,
    ContingencyMinimumValue = 13,
    HasEmploymentContingency = 14,
    EmploymentTailPeriodMonths = 15,
    BuyerCalculationPeriodDays = 16,
    SellerResponsePeriodDays = 17,
    PaymentDateOffsetDays = 18,
    PutCallRight = 19,
    PutCallExplanation = 20,
    DeferredVariableType = 21,
    CalculationDependentVariable = 22,
    CalcDvBaselineValue = 23,
    CalculationMultiple = 24,
    MaximumPayment = 25,
    PenaltyMultiple = 26,
    DistributionPeriodicity = 27,
    ExpectedTimingToRecap = 28,
    ExpectedPayoutMultiple = 29,
    AnnualDividendRate = 30,
    DividendPaymentTiming = 31,
    CompoundingFrequency = 32,
    IsConvertibleIntoCommon = 33,
    ConversionRatio = 34,
    Label = 35,
}

export type { WireInstructions, WireConfirmation };
export { ApprovalState, MarketplaceParty };

/** Everything behind the seller's "Closing Flow of Funds" engagement tab. */
export interface FlowOfFunds {
    engagementId: number;
    consideration: ConsiderationLedger;
    thirdPartyPayoffs: ThirdPartyPayoffsSection;
    /** Null until #856 populates VetValue's fee ledger. */
    vetValueFees: VetValueFeeLedger | null;
    /** Null until #857 populates the legal-fees panel. */
    legalFees: LegalFees | null;
    wireTransfers: WireTransferGroup[];
    /** Null until #859 computes the final settlement total. */
    totalDisbursementsAtClose: number | null;
    closing: ClosingCountdown;
    /** Both always present — the endpoint refuses the request before a winning buyer
     *  with a selected LOI exists, so neither can be absent in a served payload. */
    winningBuyerId: number;
    winningBuyerLoiFormId: string;
}

export interface ClosingCountdown {
    officialClosingDate: string | null;
    defaultClosingDate: string | null;
    daysRemaining: number | null;
    isEngagementClosed: boolean;
}

/* ─────────────────── Purchase Price Consideration ─────────────────── */

export interface ConsiderationValueChange {
    id: number;
    previousValue: string | null;
    newValue: string | null;
    changedBy: TimelineParty;
    changedOn: string;
    note: string | null;
    canEditNote: boolean;
}

export interface ConsiderationFieldChange {
    field: ConsiderationField;
    originalValue: string | null;
    currentValue: string | null;
    isChanged: boolean;
    history: ConsiderationValueChange[];
}

export interface ConsiderationLedger {
    rows: ConsiderationLedgerRow[];
    total: ConsiderationAmounts;
    detail: ConsiderationDetail;
    feeInputs: ConsiderationFeeInputs;
    viewerParty: TimelineParty | null;
    canAddRows: boolean;
}

/** One fee-eligible consideration type's face/fair/cash-flow inputs — maps onto
 *  ConsiderationFeeRowInput (consideration-fees.ts) via considerationType → type. */
export interface ConsiderationFeeRow {
    considerationType: TypeOfConsideration;
    faceValue: number | null;
    fairValue: number | null;
    projectedCashFlow: number | null;
}

export interface ConsiderationFeeInputs {
    /** One entry per fee-eligible consideration type, including PreferredEquity
     *  and CommonEquity, which have no ledger row of their own. */
    rows: ConsiderationFeeRow[];
    considerationRows: TypesOfConsiderationRow[];
    vvConnectFees: VvConnectFeesDto | null;
    levelOfService: LevelOfService;
    sfasParams: SfasFeeParams | null;
}

/** The eight numeric columns of the consideration summary ledger.
 *  `null` renders as an em dash — the value does not apply to that row. */
export interface ConsiderationAmounts {
    faceValue: number | null;
    fairValue: number | null;
    projectedCashFlow: number | null;
    projectedFees: number | null;
    netOfFees: number | null;
    liabilityPayoffs: number | null;
    employeePortion: number | null;
    buyerEmployeePortion: number | null;
    netToOwner: number | null;
}

export interface ConsiderationLedgerRow extends ConsiderationAmounts {
    considerationType: TypeOfConsideration;
    /** Null while the seller has not yet chosen a treatment (ConsiderationTreatment.Choice). */
    feePaymentTiming: ConsiderationTreatment | null;
    /** Rows nested under a header-only type (Earnout / Synthetic JV Equity under
     *  Deferred Variable Payments) — see isHeaderOnlyConsiderationType. */
    isSubRow: boolean;
    /** Projected fees carry a breakdown the UI exposes on the info glyph. */
    hasFeeBreakdown: boolean;
    /** Fees do not apply to this consideration type (renders "NA", not a dash). */
    feesNotApplicable: boolean;
    version: string | null;
    editableFields: ConsiderationField[];
    fieldChanges: ConsiderationFieldChange[];
    buyerApproval: ApprovalState;
    sellerApproval: ApprovalState;
}

/* ─────────────────── Consideration Detail ─────────────────── */

/** Per-consideration-type term tables shown below the summary ledger. */
export interface ConsiderationDetail {
    deferredFixedPayments: DeferredFixedPaymentTerm[];
    deferredVariablePayments: DeferredVariablePaymentTerm[];
    jvEquity: JvEquityTerm | null;
    commonCorporateEquity: CommonEquityTerm | null;
    preferredCorporateEquity: PreferredEquityTerm | null;
}

export enum PutCallRight {
    Na = 0,
    BuyerCall = 1,
    SellerPut = 2,
    BuyerCallAndSellerPut = 3,
}

export interface NegotiatedTerm {
    originalValue: number | null;
    currentValue: number | null;
}

export interface ConsiderationTermBase {
    id: number;
    valueAtClosing: number | null;
    adminComment: string | null;
    buyerComment: string | null;
    buyerCalculationPeriodDays: NegotiatedTerm;
    sellerResponsePeriodDays: NegotiatedTerm;
    paymentDateOffsetDays: NegotiatedTerm;
    putCallRight: PutCallRight;
    putCallExplanation: string | null;
    buyerApproval: ApprovalState;
    sellerApproval: ApprovalState;
    version: string | null;
    rowState: ConsiderationRowState;
    sellerComment: string | null;
    editableFields: ConsiderationField[];
    fieldChanges: ConsiderationFieldChange[];
    canRequestRemoval: boolean;
    canEditComment: boolean;
}

export interface DeferredFixedPaymentTerm extends ConsiderationTermBase {
    label: string;
    paymentTiming: string;
    amount: number;
    annualInterestAccrualRate: number | null; // percent, e.g. 6
    isContingent: boolean;
    contingencyDependentVariable: string | null;
    contingencyMinimumValue: number | null;
    hasEmploymentContingency: boolean;
    employmentTailPeriodMonths: number | null;
}

export interface DeferredVariablePaymentTerm extends ConsiderationTermBase {
    label: string;
    paymentTiming: string;
    /** Earnout or Synthetic JV Equity — the two variable sub-types. */
    considerationType: TypeOfConsideration;
    calculationDependentVariable: string | null;
    calcDvBaselineValue: number | null;
    calculationMultiple: number | null;
    maximumPayment: number | null;
    hasEmploymentContingency: boolean;
    employmentTailPeriodMonths: number | null;
    penaltyMultiple: number | null;
    distributionPeriodicity: string | null;
}

export interface JvEquityTerm extends ConsiderationTermBase {
    paymentTiming: string;
    calculationDependentVariable: string | null;
    calcDvBaselineValue: number | null;
    calculationMultiple: number | null;
    hasEmploymentContingency: boolean;
    employmentTailPeriodMonths: number | null;
    penaltyMultiple: number | null;
    distributionPeriodicity: string | null;
}

export interface CommonEquityTerm extends ConsiderationTermBase {
    expectedTimingToRecap: string;
    /** Expected payout as a multiple of current value, e.g. 2.0. */
    expectedPayoutMultiple: number | null;
}

export interface PreferredEquityTerm extends ConsiderationTermBase {
    annualDividendRate: number | null; // percent
    dividendPaymentTiming: string | null;
    compoundingFrequency: string | null;
    expectedTimingToRecap: string;
    isConvertibleIntoCommon: boolean;
    /** e.g. 1.5 for a 1.5 : 1 conversion. */
    conversionRatio: number | null;
}

/* ─────────────────── Consideration Requests ─────────────────── */

export interface ConsiderationFieldValue {
    field: ConsiderationField;
    value: string | null;
}

export interface SaveConsiderationRowRequest {
    version: string | null;
    values: ConsiderationFieldValue[];
}

export interface SaveConsiderationCommentRequest {
    comment: string | null;
}

export interface SaveConsiderationChangeNoteRequest {
    note: string | null;
}

export interface AddConsiderationRowRequest {
    values: ConsiderationFieldValue[];
}

export interface RequestConsiderationRowRemovalRequest {
    version: string | null;
}

/* ─────────────────── 3rd Party Pay-Offs ─────────────────── */

/** The section IS the DAQI vendor-contracts table (columns admin-configured
 *  per engagement) plus the FoF-appended columns on each row. Mirrors
 *  ThirdPartyPayoffsSectionContract. */
export interface ThirdPartyPayoffsSection {
    dataRequestProjectId: number | null;
    dataTableId: number | null;
    columns: VendorContractColumn[];
    /** The DAQI file column (contract/agreement upload) — one of `columns`,
     *  rendered via DataTableCellUploadPopup rather than a generic cell editor. */
    fileColumnId: number | null;
    canAddRow: boolean;
    rows: ThirdPartyPayoff[];
    /** Which side of the deal the current viewer is, resolved server-side from
     *  FlowOfFundsContext.Party — null for advisor/admin/observer. One value per
     *  request, so it lives here, not per row. Never re-derive this in React
     *  (revision 2 — replaces the web's earlier, incorrect use of
     *  useCurrentMarketplaceParty, which misclassifies a seller who is also a
     *  broker on their own engagement). */
    viewerParty: TimelineParty | null;
}

/** One DAQI vendor-contracts column, rendered generically. Mirrors
 *  VendorContractColumnContract. `answerType` matches AnswerTypeVariableNamesList
 *  ('Text' | 'Pick from list' | 'Number' | 'Date' | 'File'). */
export interface VendorContractColumn {
    columnId: number;
    customColumnId: string;
    name: string;
    answerType: string;
    /** DAQI's own FormType (e.g. 'DropDown', 'YesNo') — informational; whether
     *  to render a `select` is driven by `options` being non-empty. */
    formType: string | null;
    /** The pick-list values, resolved server-side from the column's FormList
     *  project variable. Empty for non-pick-list columns. */
    options: string[];
}

/** One DAQI cell on a row, keyed to its column by `customColumnId`. Mirrors
 *  VendorContractCellContract. `version` is DAQI's own cell concurrency token,
 *  base64-encoded — carry it through unmodified on cell writes. */
/** A file in the DAQI file column. Mirrors VendorContractFileContract. */
export interface VendorContractFile {
    fileId: number;
    name: string;
}

export interface VendorContractCell {
    cellId: number;
    customColumnId: string;
    answerText: string | null;
    version: string;
}

/** A commit from `DaqiCellEditor`, carrying the column identity so the write
 *  path can create a cell as well as update one. `cellId`/`version` are null
 *  when the row has no existing cell for this column yet (a freshly added
 *  row, or a DAQI row missing this column) — the write endpoint must treat
 *  that as a create, never a fabricated id. */
export interface VendorContractCellCommit {
    customColumnId: string;
    cellId: number | null;
    version: string | null;
    answerText: string;
}

export interface SaveThirdPartyPayoffRowRequest {
    cells: VendorContractCellCommit[];
    /** False when the editor never touched the field: the API then keeps the stored
     *  value instead of taking this (possibly stale) client one. */
    isPayoffDateProvided: boolean;
    payoffDate: string | null;
    isPerDiemProvided: boolean;
    perDiem: number | null;
}

/** `cells` carries ONLY the cells that were in the save request, post-save
 *  with fresh versions — DAQI bumps a cell's version only when that cell's
 *  answer actually changes, so the versions the store holds for unedited
 *  cells stay valid. The store patches these in by `customColumnId`;
 *  replacing the row's cells wholesale would drop every cell the save did
 *  not touch. `row` is the post-D18-reset row (approvals + capabilities),
 *  with the composition-owned fields at their defaults. */
export interface SaveThirdPartyPayoffRowResponse {
    cells: VendorContractCell[];
    row: ThirdPartyPayoff;
}

/** Server-computed, per-row disabled/hidden state — never re-derive the
 *  viewer's party or the lock in React. Mirrors ThirdPartyPayoffCapabilitiesContract. */
export interface ThirdPartyPayoffCapabilities {
    canEditCells: boolean;
    canEditPayoffDetails: boolean;
    canEditWireInstructions: boolean;
    canEditSupportingDocument: boolean;
    /** Admin-only: sets the LOI's assume/pay-off decision directly (create-or-update),
     *  needed for rows added after the LOI was submitted. */
    canEditBuyerAssumption: boolean;
    /** Admin-only, same rule as the assumption — the LOI's estimated pay-off amount. */
    canEditPayoffAmount: boolean;
    /** Same rule as canEditPayoffAmount — requires an uploaded pay-off letter. */
    canEditPerDiem: boolean;
    canApprove: boolean;
    canProposeRemoval: boolean;
    canRespondToRemoval: boolean;
    canDeleteRow: boolean;
    isLocked: boolean;
}

export interface ThirdPartyPayoff {
    id: number;
    /** The DAQI DataTableRow.Id — the row's public identity (D1). */
    tableRowId: number;
    /** The DAQI columns' values for this row, keyed by `customColumnId`. */
    cells: VendorContractCell[];
    /** The files in the DAQI file column (contract documents), in upload order —
     *  composition-owned like `cells`; write responses leave it empty. */
    contractDocuments: VendorContractFile[];
    /** Read-through from TermFormVendorContractRow.IsAssumedByBuyer (LOI-owned) —
     *  drives the Supporting Document slot's expected kind (D11). */
    isAssumedByBuyer: boolean;
    /** Read-through from the LOI; never written from this table. */
    payoffAmount: number | null;
    /** The accrued settlement figure, frozen once the row is agreed or paid.
     *  Null while it is still recomputing against the closing date. */
    settledAmount: number | null;
    payoffDate: string | null; // ISO date
    perDiem: number | null;
    wireInstructions: WireInstructions | null;
    /** The lender's payoff quote — the applicable slot when the buyer does NOT
     *  assume the obligation. */
    payoffLetterDocument: UploadedDocument | null;
    /** The vendor's letter confirming the obligation is assumable — the
     *  applicable slot when the buyer assumes it. */
    payoffConfirmationDocument: UploadedDocument | null;
    buyerApproval: ApprovalState;
    sellerApproval: ApprovalState;
    /** Null until one party proposes taking the row off the funds flow. */
    removalProposal: RemovalProposal | null;
    capabilities: ThirdPartyPayoffCapabilities;
}

export enum RemovalProposalStatus {
    Pending = 0,
    Approved = 1,
    Rejected = 2,
}

/** Ordinals mirror the C# ThirdPartyPayoffDocumentKind — do not renumber. */
export enum ThirdPartyPayoffDocumentKind {
    PayoffLetter = 0,
    PayoffConfirmation = 1,
}

export interface RemovalProposal {
    proposedBy: TimelineParty;
    /** Required — the modal will not submit without it. */
    comment: string;
    proposedOn: string; // ISO date-time
    status: RemovalProposalStatus;
}

/* ─────────────────── VetValue Fees ─────────────────── */

export interface VetValueFeeLedger {
    transactionFeePercent: number;
    projectedTransactionValue: number;
    projectedTransactionFee: number;
    applicationFee: FeeLineItem;
    monthlyRetainerFees: FeeLineItem;
    /** Buyer ID NDA, PIP/PIQ, DFM provision, buyer meeting credits — nested rows. */
    processFees: FeeLineItem[];
    lateDelayFees: FeeLineItem;
    engagementSuspensionFees: FeeLineItem;
    totalCredits: number;
    netProjectedTotalFees: number;
    portionPaidAtClose: number;
    agreement: FeeAgreement;
}

export interface FeeLineItem {
    label: string;
    fee: number | null;
    credit: number | null;
}

/** Two-party sign-off block shared by the VetValue-fee and legal-fee panels. */
export interface FeeAgreement {
    parties: FeeAgreementParty[];
}

export interface FeeAgreementParty {
    label: string;
    hasAgreed: boolean;
    agreedByName: string | null;
    /** The module cannot resolve display names; the composition layer resolves
     *  `agreedByName` from this id. */
    agreedByUserId: number | null;
    agreedOn: string | null; // ISO date-time
    /** Null for the VetValue-fee panel, which has not defined its seats yet (#856). */
    seat: LegalFeeAgreementSeat | null;
    state: ApprovalState;
    /** Server-computed: may the current viewer act on this seat. */
    canAct: boolean;
    /** The counsel seat was filled by admin/advisor because no counsel exists. */
    isOnBehalf: boolean;
    /** Server-computed: this party is the viewer's, and has not agreed yet. */
    canAgree: boolean;
}

/* ─────────────────── Legal Fees ─────────────────── */

/** Ordinals mirror the C# enum. Starts at 1 deliberately: `viewerSeat` is
 *  nullable, and a 0 member would make the counsel seat falsy. */
export enum LegalFeeAgreementSeat {
    SellersCounsel = 1,
    Seller = 2,
}

export interface LegalFeesCapabilities {
    canEditInvoice: boolean;
    canEditAmount: boolean;
    canEditWireInstructions: boolean;
    canVerifyWireInstructions: boolean;
    canAgreeAsCounsel: boolean;
    canAgreeAsSeller: boolean;
    isAgreedByBothSeats: boolean;
}

export interface WireInstructionsVerification {
    verifiedOn: string; // ISO date-time
    verifiedByName: string;
    verifiedByUserId: number | null;
}

export interface LegalFees {
    invoiceDocument: UploadedDocument | null;
    /** Null until counsel enters it — distinct from an agreed $0.00. */
    amountOwedAtClose: number | null;
    wireInstructions: WireInstructions | null;
    /** Null until an admin marks the instructions verified. */
    wireInstructionsVerification: WireInstructionsVerification | null;
    agreement: FeeAgreement;
    /** Server-computed; never re-derived in React. */
    capabilities: LegalFeesCapabilities;
    /** Which agreement seat the viewer holds, or null (admin, advisor, buyer). */
    viewerSeat: LegalFeeAgreementSeat | null;
}

/* ─────────────────── Wire Transfer Summary ─────────────────── */

export enum WireTransferStatus {
    PaymentAmountPending = 0,
    PaymentAmountAgreed = 1,
    PaymentSent = 2,
    PaymentReceived = 3,
}

export enum WireTransferCategory {
    Seller = 0,
    ThirdPartyPayoff = 1,
    LegalFees = 2,
    VetValueFees = 3,
}

export interface WireTransferGroup {
    sourceRowId: number | null;
    category: WireTransferCategory;
    categoryDescription: string;
    recipientName: string;
    recipientDescription: string;
    amount: number | null;
    status: WireTransferStatus;
    wireInstructions: WireInstructions | null;
    paymentDate: string | null; // ISO date
    canConfirmSent: boolean;
    canConfirmReceipt: boolean;
    canUploadEvidence: boolean;
    canManageSenderEvidence: boolean;
    canManageReceiptEvidence: boolean;
    /** Admin-only: this block's wire instructions are editable by the viewer. */
    canEditWireInstructions: boolean;
    sentConfirmation: WireConfirmation | null;
    receiptConfirmation: WireConfirmation | null;
    senderEvidenceDocument: UploadedDocument | null;
    receiptEvidenceDocument: UploadedDocument | null;
    payoffConfirmationDocument: UploadedDocument | null;
}

/* ─────────────────── Shared ─────────────────── */

export interface UploadedDocument {
    id: number;
    fileName: string;
    fileSizeBytes: number;
    contentType: string;
    pageCount: number | null;
    uploadedOn: string; // ISO date-time
}
